import axios, { AxiosInstance, AxiosError } from 'axios';

// API Base URL from environment
// Debug: Check all environment variables
console.log("🔍 Environment Variables Debug:", {
  'import.meta.env': import.meta.env,
  'VITE_API_BASE_URL': import.meta.env.VITE_API_BASE_URL,
  'MODE': import.meta.env.MODE,
  'DEV': import.meta.env.DEV,
  'PROD': import.meta.env.PROD
});

// Use the value from vite config if available, otherwise fallback to import.meta.env
// @ts-ignore - This is defined in vite.config.ts
let API_BASE_URL = typeof __VITE_API_BASE_URL__ !== 'undefined'
  ? __VITE_API_BASE_URL__
  : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:4017');

// Convert HTTPS to HTTP for localhost in development (to avoid SSL errors)
// Keep HTTPS for production domains like rita366.com
if (import.meta.env.DEV && API_BASE_URL.startsWith('https://') && API_BASE_URL.includes('localhost')) {
  API_BASE_URL = API_BASE_URL.replace('https://', 'http://');
  console.warn('⚠️ Converted HTTPS to HTTP for localhost development:', API_BASE_URL);
}

console.log("✅ Final API_BASE_URL:", API_BASE_URL);

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Function to update default Authorization header (exported for use after login)
export const updateAuthHeader = (token: string | null) => {
  if (token && token.trim()) {
    const authValue = `Bearer ${token.trim()}`;
    // Set default header for all requests
    apiClient.defaults.headers.common['Authorization'] = authValue;
    console.log('✅ Default Authorization header updated:', authValue.substring(0, 40) + '...');
  } else {
    // Remove default header if no token
    delete apiClient.defaults.headers.common['Authorization'];
    console.log('⚠️ Default Authorization header removed');
  }
};

// Initialize auth header on module load (when API client is imported)
const initializeAuthHeader = () => {
  try {
    const initialToken = localStorage.getItem('authToken');
    console.log('🔧 Initializing auth header on module load:', {
      hasToken: !!initialToken,
      tokenLength: initialToken?.length || 0,
      tokenPreview: initialToken ? initialToken.substring(0, 30) + '...' : 'No token',
      timestamp: new Date().toISOString()
    });

    if (initialToken && initialToken.trim() && initialToken.length > 10) {
      // Remove "Bearer " prefix if present
      let tokenString = initialToken.trim();
      if (tokenString.startsWith('Bearer ')) {
        tokenString = tokenString.substring(7).trim();
      }
      updateAuthHeader(tokenString);
      console.log('✅ Auth header initialized successfully');
    } else {
      console.warn('⚠️ No valid token found during initialization');
    }
  } catch (error) {
    console.error('❌ Error initializing auth header:', error);
  }
};

// Initialize on module load
initializeAuthHeader();

// Expose apiClient to window for debugging (development only)
if (import.meta.env.DEV) {
  (window as any).apiClient = apiClient;
  console.log('🔍 apiClient exposed to window.apiClient for debugging');
}

// Helper function to get token from localStorage
const getAuthToken = (): string | null => {
  try {
    // Direct read from localStorage - most reliable
    const token = localStorage.getItem('authToken');

    // Validate token
    if (token && typeof token === 'string') {
      const trimmed = token.trim();
      // Check if it's a valid token string
      if (trimmed.length > 10 && // Minimum token length
        trimmed !== 'null' &&
        trimmed !== 'undefined' &&
        !trimmed.startsWith('[object')) {
        return trimmed;
      }
    }

    return null;
  } catch (error) {
    console.error('❌ Error reading token from localStorage:', error);
    return null;
  }
};

// Request interceptor - Add Bearer token to requests
apiClient.interceptors.request.use(
  async (config) => {
    // Small delay to ensure localStorage is ready after refresh
    let token = localStorage.getItem('authToken');
    let retries = 0;

    while ((!token || token.length < 10) && retries < 20) {
      await new Promise((res) => setTimeout(res, 5));
      token = localStorage.getItem('authToken');
      retries++;
    }

    // Public routes → don't require token, but still add if available
    const publicRoutes = ['/login', '/forgot-password', '/register'];
    const isPublicRoute = publicRoutes.some((p) => config.url?.includes(p));
    const isContactRoute = config.url?.includes('/contact/create-submission');

    // Public blog API routes - allow without token
    const isPublicBlogRoute = config.url?.includes('/api/blogs') ||
      config.url?.includes('/blogs/featured') ||
      config.url?.includes('/blogs/categories') ||
      config.url?.includes('/blogs/search') ||
      config.url?.includes('/blogs/stats');

    // Public testimonials API routes - allow without token
    const isPublicTestimonialRoute = config.url?.includes('/api/testimonials');

    if (isPublicRoute) {
      return config;
    }

    // For contact route, add token if available but don't block if missing
    if (isContactRoute) {
      if (token) {
        if (!config.headers) config.headers = {} as any;
        (config.headers as any).Authorization = `Bearer ${token.trim()}`;
      }
      return config;
    }

    // For public blog routes, add token if available but don't block if missing
    if (isPublicBlogRoute) {
      if (token) {
        if (!config.headers) config.headers = {} as any;
        (config.headers as any).Authorization = `Bearer ${token.trim()}`;
      }
      return config;
    }

    // Public store API routes - allow without token
    const isPublicStoreRoute = config.url?.includes('/api/stores') ||
      config.url?.includes('/api/categories');

    // Public jobs/careers API routes - allow without token
    const isPublicJobRoute = config.url?.includes('/api/jobs') ||
      config.url?.includes('/api/jobs/filters') ||
      config.url?.includes('/api/jobs/apply');

    // For public testimonial routes, add token if available but don't block if missing
    if (isPublicTestimonialRoute) {
      if (token) {
        if (!config.headers) config.headers = {} as any;
        (config.headers as any).Authorization = `Bearer ${token.trim()}`;
      }
      return config;
    }

    // For public store routes, add token if available but don't block if missing
    if (isPublicStoreRoute) {
      if (token) {
        if (!config.headers) config.headers = {} as any;
        (config.headers as any).Authorization = `Bearer ${token.trim()}`;
      }
      return config;
    }

    // For public job routes, add token if available but don't block if missing
    if (isPublicJobRoute) {
      if (token) {
        if (!config.headers) config.headers = {} as any;
        (config.headers as any).Authorization = `Bearer ${token.trim()}`;
      }
      return config;
    }

    // If still no token → block request (prevents 401 spam)
    if (!token) {
      console.warn('⛔ Token missing after waiting. Blocking request:', config.url);
      return Promise.reject({
        response: {
          status: 401,
          message: 'Unauthorized: No token provided',
        },
      });
    }

    // Add Bearer token
    if (!config.headers) config.headers = {} as any;
    (config.headers as any).Authorization = `Bearer ${token.trim()}`;

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Handle cases where status is 401 inside a successful response (success: false)
    if (response.data && response.data.success === false && response.data.status === 401) {
      console.warn('🔓 Unauthorized detected in response body, forcing logout.');
      localStorage.clear();
      window.location.replace('/admin/login');
      return response;
    }
    return response;
  },
  (error: AxiosError<any>) => {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          const isLoginPage = window.location.pathname.includes('/login');
          const hasToken = !!getAuthToken();

          const msg = (data?.message || '').toLowerCase();
          const isNoTokenError =
            msg.includes('no token') ||
            msg.includes('token missing') ||
            msg.includes('unauthorized') ||
            msg.includes('jwt') ||
            msg.includes('invalid signature') ||
            msg.includes('expired') ||
            status === 401;

          if (!isLoginPage) {
            // Case 1: Token exists but server says unauthorized (due to refresh timing)
            if (isNoTokenError && hasToken) {
              console.warn('⚠️ Ignoring 401 due to refresh timing, token exists.');
              return Promise.reject(error);
            }

            // Case 2: Truly no token → redirect
            if (!hasToken) {
              console.log('⛔ No token found, redirecting to login…');
              localStorage.clear();
              window.location.replace('/admin/login');
              return;
            }

            // Case 3: Token invalid or expired → logout
            console.log('🔓 Token expired/invalid, forcing logout.');
            localStorage.clear();
            window.location.replace('/admin/login');
          }
          break;
        case 403:
          console.error('Access denied');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Server error');
          break;
        default:
          console.error('API Error:', data?.message || error.message);
      }
    } else if (error.request) {
      // Network error - no response received
      const isConnectionRefused = error.code === 'ERR_CONNECTION_REFUSED' ||
        error.message?.includes('ERR_CONNECTION_REFUSED') ||
        error.message?.includes('net::ERR_CONNECTION_REFUSED');
      const isNetworkError = error.code === 'ERR_NETWORK' ||
        error.message?.includes('Network Error');

      let errorMessage = 'Network error - please check your connection';

      if (isConnectionRefused) {
        errorMessage = 'Cannot connect to server. Please ensure the backend server is running and accessible.';
        console.error('❌ Connection Refused:', {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          fullURL: (error.config?.baseURL || '') + (error.config?.url || ''),
          message: 'The backend server is not running or not accessible at this address'
        });
      } else if (isNetworkError) {
        errorMessage = 'Network error - please check your internet connection and server status.';
        console.error('❌ Network Error:', {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          code: error.code,
          message: error.message
        });
      } else {
        console.error('❌ Request Error:', {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          code: error.code,
          message: error.message
        });
      }

      // Attach a user-friendly message to the error
      (error as any).userMessage = errorMessage;
      (error as any).isNetworkError = true;
      (error as any).isConnectionRefused = isConnectionRefused;
    }

    return Promise.reject(error);
  }
);

export default apiClient;
